import {Button} from '@dropbox/dig-components/buttons';
import {Menu} from '@dropbox/dig-components/menu';
import {Modal} from '@dropbox/dig-components/modal';
import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CheckmarkLine, FilterLine} from '@dropbox/dig-icons/assets';
import {useMutation, useSuspenseQuery} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {
  ApiError,
  CeleryMetataskService,
  CeleryTaskMeta,
  RoadmapSyncTriggerMode,
  ToolsService,
} from 'client';
import {Layout} from 'components/DSYS/Layout';
import {Table} from 'components/DSYS/Table';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {isSuperUser} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useAtomValue} from 'jotai';
import {Suspense, useMemo, useState} from 'react';
import {redirect} from 'react-router';
import {getService} from 'utilities';

import {TaskStatusLine} from './TaskStatusLine';

const columns = [
  {type: 'id', minWidth: 20},
  {type: 'task_id', minWidth: 100},
  {type: 'status', minWidth: 100},
  {type: 'date_done', minWidth: 100},
  {type: 'traceback', minWidth: 550},
  {type: 'name', minWidth: 350},
  {type: 'worker', minWidth: 100},
  {type: 'retries', minWidth: 100},
];

const ALL_TASKS_FILTER = 'all';
const TASKS_WITHOUT_NAME_FILTER = '__tasks_without_name__';
const UPDATE_EMPLOYEES_TASK_ID_KEY = 'update_employees_last_fetched_task_id';

const queuedTask = (label: string) => ({
  label,
  getStatus: async (taskId: string) => {
    const result = await getService(ToolsService).getTaskStatusApiV1ToolTaskStatusTaskIdGet(taskId);
    return result.status === 'completed' && result.message
      ? {...result, message: `${label} completed (${result.message}).`}
      : result;
  },
});

const TRACKED_TASKS = {
  roadmap_incremental: queuedTask('Roadmap incremental sync'),
  roadmap_full: queuedTask('Roadmap full rebuild'),
  metric_snapshot: queuedTask('Workstream metric snapshot'),
  dcd_ownership: queuedTask('DCD ownership sync'),
  employees: {
    label: 'Employee sync',
    getStatus: (taskId: string) =>
      getService(ToolsService).getUpdateEmployeesStatusApiV1ToolUpdateEmployeesTaskIdGet(taskId),
  },
  servicenow: {
    label: 'ServiceNow content sync',
    getStatus: (taskId: string) =>
      getService(
        ToolsService
      ).getServicenowContentSyncStatusApiV1ToolServicenowContentSyncTaskIdGet(taskId),
  },
};

type WatchedTask = {kind: keyof typeof TRACKED_TASKS; taskId: string; queuedAt?: number};

const getTaskFilterValue = (task: CeleryTaskMeta) => task.name?.trim() || TASKS_WITHOUT_NAME_FILTER;

const getTaskFilterLabel = (value: string) =>
  value === TASKS_WITHOUT_NAME_FILTER ? 'No task name' : value;

const getTaskCountLabel = (count: number) => `${count} task${count === 1 ? '' : 's'}`;

export const CeleryTaskMetaAdmin = () => {
  const pulseUser = useAtomValue(pulseUserAtom);
  useDocumentTitle('Tasks');

  if (!pulseUser) {
    return <></>;
  }

  if (!isSuperUser(pulseUser)) {
    redirect('/');
  }

  return (
    <Layout.Container
      width="100%"
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {children: 'Tasks', to: ROUTE_PATHS.ADMIN_CELERY_TASKS},
      ]}
    >
      <Title size={24}>Tasks</Title>

      <TaskActions />

      <Suspense fallback={null}>
        <TaskMetaTableView />
      </Suspense>
    </Layout.Container>
  );
};

export const TaskActions = () => {
  const [showFullRebuildConfirmation, setShowFullRebuildConfirmation] = useState(false);
  const [showServiceNowSyncConfirmation, setShowServiceNowSyncConfirmation] = useState(false);
  const [watchedTask, setWatchedTask] = useState<WatchedTask | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<string>();

  const updateEmployeesMutation = useMutation({
    mutationFn: () => getService(ToolsService).updateEmployeesApiV1ToolUpdateEmployeesPut(),
    onSuccess: (result) => {
      localStorage.setItem(UPDATE_EMPLOYEES_TASK_ID_KEY, result.task_id);
      setWatchedTask({kind: 'employees', taskId: result.task_id, queuedAt: Date.now()});
    },
    onError: (error) => {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to trigger employee sync.');
    },
  });

  const serviceNowCheckMutation = useMutation({
    mutationFn: () =>
      getService(ToolsService).checkServicenowCredentialsApiV1ToolCheckServicenowCredentialsPost(),
    onSuccess: (result) => {
      setStatusMessage(result.message);
    },
    onError: (error) => {
      setErrorMessage(
        error instanceof ApiError && typeof error.body?.detail === 'string'
          ? error.body.detail
          : error instanceof Error
            ? error.message
            : 'Failed to check ServiceNow credentials.'
      );
    },
  });

  const serviceNowSyncMutation = useMutation({
    mutationFn: () =>
      getService(ToolsService).triggerServicenowContentSyncApiV1ToolServicenowContentSyncPost(),
    onSuccess: (result) => {
      setShowServiceNowSyncConfirmation(false);
      setWatchedTask({kind: 'servicenow', taskId: result.task_id, queuedAt: Date.now()});
    },
    onError: (error) => {
      setErrorMessage(
        error instanceof ApiError && typeof error.body?.detail === 'string'
          ? error.body.detail
          : error instanceof Error
            ? error.message
            : 'Failed to trigger ServiceNow content sync.'
      );
    },
  });

  const roadmapSyncMutation = useMutation({
    mutationFn: (mode: RoadmapSyncTriggerMode) =>
      getService(ToolsService).triggerRoadmapSyncApiV1ToolRoadmapSyncModePost(mode),
    onSuccess: (result) => {
      setShowFullRebuildConfirmation(false);
      setWatchedTask({
        kind: result.mode === RoadmapSyncTriggerMode.FULL ? 'roadmap_full' : 'roadmap_incremental',
        taskId: result.task_id,
        queuedAt: Date.now(),
      });
    },
    onError: (error) => {
      setErrorMessage(
        error instanceof ApiError && typeof error.body?.detail === 'string'
          ? error.body.detail
          : error instanceof Error
            ? error.message
            : 'Failed to trigger Roadmap sync.'
      );
    },
  });

  const workstreamMetricSnapshotMutation = useMutation({
    mutationFn: () =>
      getService(
        ToolsService
      ).triggerWorkstreamMetricSnapshotApiV1ToolWorkstreamMetricSnapshotsPost(),
    onSuccess: (result) => {
      setWatchedTask({kind: 'metric_snapshot', taskId: result.task_id, queuedAt: Date.now()});
    },
    onError: (error) => {
      setErrorMessage(
        error instanceof ApiError && typeof error.body?.detail === 'string'
          ? error.body.detail
          : error instanceof Error
            ? error.message
            : 'Failed to trigger Workstream Metric snapshot.'
      );
    },
  });

  const dcdOwnershipSyncMutation = useMutation({
    mutationFn: () =>
      getService(ToolsService).triggerDcdOwnershipSyncApiV1ToolDcdOwnershipSyncPost(),
    onSuccess: (result) => {
      setWatchedTask({kind: 'dcd_ownership', taskId: result.task_id, queuedAt: Date.now()});
    },
    onError: (error) => {
      setErrorMessage(
        error instanceof ApiError && typeof error.body?.detail === 'string'
          ? error.body.detail
          : error instanceof Error
            ? error.message
            : 'Failed to trigger DCD ownership sync.'
      );
    },
  });

  const handleTaskAction = (action: string) => {
    // Clear previous messages and errors
    setErrorMessage(undefined);
    setStatusMessage(undefined);
    setWatchedTask(null);

    if (action === 'update_employees') {
      updateEmployeesMutation.mutate();
    } else if (action === 'check_latest_employee_sync') {
      const taskId = localStorage.getItem(UPDATE_EMPLOYEES_TASK_ID_KEY);
      if (taskId) {
        setWatchedTask({kind: 'employees', taskId});
      } else {
        setErrorMessage('Trigger an employee sync before checking its status.');
      }
    } else if (action === 'check_servicenow_credentials') {
      serviceNowCheckMutation.mutate();
    } else if (action === 'sync_servicenow_content') {
      setShowServiceNowSyncConfirmation(true);
    } else if (action === 'sync_roadmap_incremental') {
      roadmapSyncMutation.mutate(RoadmapSyncTriggerMode.INCREMENTAL);
    } else if (action === 'sync_roadmap_full') {
      setShowFullRebuildConfirmation(true);
    } else if (action === 'capture_workstream_metric_snapshot') {
      workstreamMetricSnapshotMutation.mutate();
    } else if (action === 'sync_dcd_ownership') {
      dcdOwnershipSyncMutation.mutate();
    }
  };

  const triggerFullRoadmapRebuild = () => {
    setErrorMessage(undefined);
    setStatusMessage(undefined);
    roadmapSyncMutation.mutate(RoadmapSyncTriggerMode.FULL);
  };

  const triggerServiceNowContentSync = () => {
    setErrorMessage(undefined);
    setStatusMessage(undefined);
    serviceNowSyncMutation.mutate();
  };

  const isPending =
    updateEmployeesMutation.isPending ||
    serviceNowCheckMutation.isPending ||
    serviceNowSyncMutation.isPending ||
    roadmapSyncMutation.isPending ||
    workstreamMetricSnapshotMutation.isPending ||
    dcdOwnershipSyncMutation.isPending;

  return (
    <>
      <Box
        borderRadius="Large"
        borderWidth="1"
        borderStyle="Solid"
        borderColor="Border Subtle"
        padding="16"
        marginTop="16"
      >
        <Stack gap="8">
          <Box>
            <Text size="small">Run approved maintenance actions and check their status.</Text>
          </Box>
          <Split alignY="center" gap="8" wrap="wrap">
            <Split.Item>
              <Menu.Wrapper closeOnSelection onSelection={handleTaskAction}>
                {({getContentProps, getTriggerProps}) => (
                  <>
                    <Button
                      {...getTriggerProps()}
                      variant="primary"
                      withDropdownIcon
                      disabled={isPending}
                    >
                      Trigger task
                    </Button>
                    <Menu.Content {...getContentProps()} placement="bottom-start">
                      <Menu.Segment withLabel="Roadmap">
                        <Menu.ActionItem
                          value="sync_roadmap_incremental"
                          withTitle="Trigger incremental Roadmap sync"
                          withSubtitle="Sync Jira issues updated since the last successful run."
                        />
                        <Menu.ActionItem
                          value="sync_roadmap_full"
                          withTitle="Trigger full Roadmap rebuild"
                          withSubtitle="Re-read all Roadmap issues from Jira and rebuild the index."
                        />
                      </Menu.Segment>
                      <Menu.Segment withLabel="Maintenance">
                        <Menu.ActionItem
                          value="update_employees"
                          withTitle="Trigger employee sync"
                          withSubtitle="Queue update_employees task."
                        />
                        <Menu.ActionItem
                          value="check_latest_employee_sync"
                          withTitle="Check status of last employee sync"
                          withSubtitle="Look up the most recently queued employee sync."
                        />
                        <Menu.ActionItem
                          value="check_servicenow_credentials"
                          withTitle="Check ServiceNow credentials"
                          withSubtitle="Validate credentials for current env without syncing content."
                        />
                        <Menu.ActionItem
                          value="sync_servicenow_content"
                          withTitle="Trigger ServiceNow content sync"
                          withSubtitle="Refresh Knowledge metadata and article content for the current environment."
                        />
                        <Menu.ActionItem
                          value="capture_workstream_metric_snapshot"
                          withTitle="Trigger Workstream Metric snapshot"
                          withSubtitle="Uses today's UTC date, which may be misaligned with the normal two-week snapshot window."
                        />
                        <Menu.ActionItem
                          value="sync_dcd_ownership"
                          withTitle="Trigger DCD ownership sync"
                          withSubtitle="Queue sync_dcd_ownership task."
                        />
                      </Menu.Segment>
                    </Menu.Content>
                  </>
                )}
              </Menu.Wrapper>
            </Split.Item>
          </Split>
          {watchedTask ? (
            <TaskStatusLine
              key={watchedTask.taskId}
              label={TRACKED_TASKS[watchedTask.kind].label}
              taskId={watchedTask.taskId}
              queuedAt={watchedTask.queuedAt}
              getStatus={TRACKED_TASKS[watchedTask.kind].getStatus}
            />
          ) : null}
          {statusMessage && (
            <Box role="status">
              <Text size="small">{statusMessage}</Text>
            </Box>
          )}
          {errorMessage ? (
            <Box role="alert">
              <Text color="error" size="small">
                {errorMessage}
              </Text>
            </Box>
          ) : null}
        </Stack>
      </Box>
      {showFullRebuildConfirmation ? (
        <Modal
          isCentered
          open
          withCloseButton="Close"
          onRequestClose={() => setShowFullRebuildConfirmation(false)}
        >
          <Modal.Header hasBottomSpacing="title-standard">
            <Modal.Title>Trigger full Roadmap rebuild?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Text>
              This re-reads every Roadmap issue from Jira and rebuilds the published Roadmap and
              dependency indexes. The current published data remains available while it runs.
            </Text>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="opacity"
              onClick={() => setShowFullRebuildConfirmation(false)}
              disabled={roadmapSyncMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={triggerFullRoadmapRebuild}
              isLoading={roadmapSyncMutation.isPending}
              disabled={roadmapSyncMutation.isPending}
            >
              Trigger full rebuild
            </Button>
          </Modal.Footer>
        </Modal>
      ) : null}
      {showServiceNowSyncConfirmation ? (
        <Modal
          isCentered
          open
          withCloseButton="Close"
          onRequestClose={() => setShowServiceNowSyncConfirmation(false)}
        >
          <Modal.Header hasBottomSpacing="title-standard">
            <Modal.Title>Trigger ServiceNow content sync?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Text>
              This fetches the current environment&apos;s ServiceNow Knowledge taxonomy and article
              metadata, updates the local cache, and removes records no longer present upstream. It
              may take several minutes.
            </Text>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="opacity"
              onClick={() => setShowServiceNowSyncConfirmation(false)}
              disabled={serviceNowSyncMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={triggerServiceNowContentSync}
              isLoading={serviceNowSyncMutation.isPending}
              disabled={serviceNowSyncMutation.isPending}
            >
              Trigger content sync
            </Button>
          </Modal.Footer>
        </Modal>
      ) : null}
    </>
  );
};

const TaskMetaTableView = () => {
  const [selectedTaskFilter, setSelectedTaskFilter] = useState(ALL_TASKS_FILTER);
  const {data: tasksData = []} = useSuspenseQuery({
    queryKey: ['celery_taskmeta'],
    queryFn: getService(CeleryMetataskService).getTasksApiV1CeleryMetataskGet,
  });
  // Filter out generate health score tasks since they cause too much noise in UI
  const tasks = tasksData?.filter((task) => !task.name?.includes('generate_and_save_health_score'));

  const taskFilterOptions = useMemo(() => {
    const taskCounts = new Map<string, number>();

    tasks.forEach((task) => {
      const taskFilterValue = getTaskFilterValue(task);
      taskCounts.set(taskFilterValue, (taskCounts.get(taskFilterValue) ?? 0) + 1);
    });

    return Array.from(taskCounts.entries())
      .map(([value, count]) => ({
        value,
        count,
        label: getTaskFilterLabel(value),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [tasks]);

  const selectedTaskFilterLabel =
    selectedTaskFilter === ALL_TASKS_FILTER ? 'All tasks' : getTaskFilterLabel(selectedTaskFilter);

  const filteredTasks = useMemo(
    () =>
      selectedTaskFilter === ALL_TASKS_FILTER
        ? tasks
        : tasks.filter((task) => getTaskFilterValue(task) === selectedTaskFilter),
    [selectedTaskFilter, tasks]
  );

  const filteredTaskCountLabel = `${filteredTasks.length} of ${tasks.length} tasks`;

  const hasTraceback = useMemo(() => {
    return filteredTasks.some((task) => !!task.traceback);
  }, [filteredTasks]);

  const hasRetries = useMemo(() => {
    return filteredTasks.some((task) => !!task.retries);
  }, [filteredTasks]);

  const tableColumns = useMemo(() => {
    let filteredColumns = [...columns];
    if (!hasTraceback) {
      filteredColumns = filteredColumns.filter((column) => column.type !== 'traceback');
    }
    if (!hasRetries) {
      filteredColumns = filteredColumns.filter((column) => column.type !== 'retries');
    }
    return filteredColumns;
  }, [hasTraceback, hasRetries]);

  return (
    <Suspense fallback={null}>
      <Stack gap="12" className={atoms({paddingTop: '24'})}>
        <Split alignY="center" gap="8">
          <Split.Item width="fill">
            <Text color="faint">{filteredTaskCountLabel}</Text>
          </Split.Item>
          <Split.Item>
            <TaskNameFilterMenu
              selectedTaskFilter={selectedTaskFilter}
              selectedTaskFilterLabel={selectedTaskFilterLabel}
              taskFilterOptions={taskFilterOptions}
              totalTaskCount={tasks.length}
              onTaskFilterChange={setSelectedTaskFilter}
            />
          </Split.Item>
        </Split>
        <Table columns={tableColumns} data={filteredTasks}>
          <Table.Header />
          <Table.Body>
            {filteredTasks.map((task, index) => (
              <Table.Row key={index}>
                <Table.Cell>
                  <Text>{task.id}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Text>{task.task_id}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Text>{task.status}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Text>{task.date_done}</Text>
                </Table.Cell>
                {hasTraceback && (
                  <Table.Cell>
                    <Text>{task.traceback}</Text>
                  </Table.Cell>
                )}
                <Table.Cell>
                  <Text>{task.name}</Text>
                </Table.Cell>
                <Table.Cell>
                  <Text>{task.worker}</Text>
                </Table.Cell>
                {hasRetries && (
                  <Table.Cell>
                    <Text>{task.retries}</Text>
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Stack>
    </Suspense>
  );
};

const TaskNameFilterMenu = ({
  selectedTaskFilter,
  selectedTaskFilterLabel,
  taskFilterOptions,
  totalTaskCount,
  onTaskFilterChange,
}: {
  selectedTaskFilter: string;
  selectedTaskFilterLabel: string;
  taskFilterOptions: {value: string; label: string; count: number}[];
  totalTaskCount: number;
  onTaskFilterChange: (taskName: string) => void;
}) => {
  const filterButtonLabel = `Task: ${selectedTaskFilterLabel}`;

  return (
    <Menu.Wrapper closeOnSelection onSelection={onTaskFilterChange}>
      {({getContentProps, getTriggerProps}) => (
        <>
          <Button
            {...getTriggerProps()}
            aria-label="Filter tasks"
            variant="outline"
            withDropdownIcon
            withIconStart={<UIIcon src={FilterLine} />}
          >
            <Truncate maxWidth={600}>{filterButtonLabel}</Truncate>
          </Button>
          <Menu.Content {...getContentProps()} placement="bottom-end" style={{minWidth: 280}}>
            <Menu.Segment withLabel="Task">
              <Menu.ActionItem
                value={ALL_TASKS_FILTER}
                withTitle="All tasks"
                withSubtitle={getTaskCountLabel(totalTaskCount)}
                withRightAccessory={
                  selectedTaskFilter === ALL_TASKS_FILTER ? (
                    <UIIcon src={CheckmarkLine} size="small" />
                  ) : undefined
                }
              />
              {taskFilterOptions.map((option) => (
                <Menu.ActionItem
                  key={option.value}
                  value={option.value}
                  withTitle={<Truncate maxWidth={240}>{option.label}</Truncate>}
                  withSubtitle={getTaskCountLabel(option.count)}
                  withRightAccessory={
                    selectedTaskFilter === option.value ? (
                      <UIIcon src={CheckmarkLine} size="small" />
                    ) : undefined
                  }
                />
              ))}
            </Menu.Segment>
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  );
};
